import { ref, reactive, toRefs } from 'vue';
import axios from 'axios';
import date from 'quasar/src/utils/date/date.js';;
import useComplexTree from '@/hooks/complexTree';

const {
  ticked, populateTreeTicks,
} = useComplexTree();

const session = reactive({
  sessionId: 0,
  sessionDetails: {
    sessionName: '',
    sessionType: '',
    startDate: '',
    endDate: '',
    applicationLockDate: '',
    isOpen: 'Closed',
    unitTypes: [],
    sessionUnitModels: [],
    sessionComplexModels: [],
    description: '',
    moveInDate: '',
    moveOutDate: '',
    applicationDeadline: '',
    confirmationDeadline: '',
    paymentDeadline: '',
    paperworkDeadline: '',
    complianceOrQuitDate: '',
    priorityDeadline: '',
    firstYearStudentMoveInDate: '',
    returningStudentMoveInDate: '',
    nonContinuingMoveOutDate: '',
    continuingMoveOutDate: '',
    priorityFirstYearConfirmationOfSpace: '',
    generalFirstYearConfirmationOfSpace: '',
    cancellationDepositRefundDeadline: '',
    genderOptions: [],
    agreement: '',
    agreementUri: '',
    dssUri: '',
    vaccinationUri: '',
    depositAmount: '',
    housingAgreementBlobId: null,
    handbookAgreementBlobId: null,
    sessionTerm: '',
    previousSessionId: null,
    eapSessionId: null,

    emailTemplateApplicationSubmittedToHousingId: '',
    emailTemplateApplicationSubmittedToStudentId: '',
    emailTemplateDssRequestedToDssId: '',
    emailTemplateDssRequestedToStudentId: '',
    emailTemplatePlacementCompletedToStudentId: '',

    postDeadlineApplicantWhitelist: [],
  },
  colleagueGenderOptions: [],
  roomRateTables: [],
});

const sessionNameIds = ref([]);
const isSessionFormValid = ref(false);
const showOnlyOpenSessions = ref(false);
const sessionIdSelected = ref(null);

export default function useSession() {
  const populateActiveComplexesAndUnits = async () => {
    session.sessionDetails.sessionComplexModels = [];
    session.sessionDetails.sessionUnitModels = [];

    for (let i = 0; i < ticked.value.length; i += 1) {
      const tickedValue = ticked.value[i];
      if (tickedValue.startsWith('Complex:')) {
        session.sessionDetails.sessionComplexModels.push({ sessionId: session.sessionId, complexId: tickedValue.substring('Complex:'.length) });
      } else {
        session.sessionDetails.sessionUnitModels.push({ sessionId: session.sessionId, unitId: tickedValue.substring('Unit:'.length) });
      }
    }
  };

  const preProcessSession = async () => {
    session.sessionDetails.unitTypes.sort();
    session.sessionDetails.sessionId = 0;
  };

  const uiPreProcessDates = () => {
    session.sessionDetails.applicationLockDate = date.formatDate(session.sessionDetails.applicationLockDate, 'MM/DD/YYYY');
    session.sessionDetails.applicationDeadline = date.formatDate(session.sessionDetails.applicationDeadline, 'MM/DD/YYYY');
    session.sessionDetails.moveInDate = date.formatDate(session.sessionDetails.moveInDate, 'MM/DD/YYYY');
    session.sessionDetails.moveOutDate = date.formatDate(session.sessionDetails.moveOutDate, 'MM/DD/YYYY');
    session.sessionDetails.confirmationDeadline = date.formatDate(session.sessionDetails.confirmationDeadline, 'MM/DD/YYYY');
    session.sessionDetails.startDate = date.formatDate(session.sessionDetails.startDate, 'MM/DD/YYYY');
    session.sessionDetails.endDate = date.formatDate(session.sessionDetails.endDate, 'MM/DD/YYYY');
    session.sessionDetails.paymentDeadline = date.formatDate(session.sessionDetails.paymentDeadline, 'MM/DD/YYYY');
    session.sessionDetails.paperworkDeadline = date.formatDate(session.sessionDetails.paperworkDeadline, 'MM/DD/YYYY');
    session.sessionDetails.complianceOrQuitDate = date.formatDate(session.sessionDetails.complianceOrQuitDate, 'MM/DD/YYYY');
    session.sessionDetails.priorityDeadline = date.formatDate(session.sessionDetails.priorityDeadline, 'MM/DD/YYYY');
    session.sessionDetails.firstYearStudentMoveInDate = date.formatDate(session.sessionDetails.firstYearStudentMoveInDate, 'MM/DD/YYYY');
    session.sessionDetails.returningStudentMoveInDate = date.formatDate(session.sessionDetails.returningStudentMoveInDate, 'MM/DD/YYYY');
    session.sessionDetails.nonContinuingMoveOutDate = date.formatDate(session.sessionDetails.nonContinuingMoveOutDate, 'MM/DD/YYYY');
    session.sessionDetails.continuingMoveOutDate = date.formatDate(session.sessionDetails.continuingMoveOutDate, 'MM/DD/YYYY');
    session.sessionDetails.priorityFirstYearConfirmationOfSpace = date.formatDate(session.sessionDetails.priorityFirstYearConfirmationOfSpace, 'MM/DD/YYYY');
    session.sessionDetails.generalFirstYearConfirmationOfSpace = date.formatDate(session.sessionDetails.generalFirstYearConfirmationOfSpace, 'MM/DD/YYYY');
    session.sessionDetails.cancellationDepositRefundDeadline = date.formatDate(session.sessionDetails.cancellationDepositRefundDeadline, 'MM/DD/YYYY');
  };

  const createSession = async () => {
    try {
      await preProcessSession();
      await populateActiveComplexesAndUnits();
      const response = await axios.post(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions`,
        session.sessionDetails,
      );

      return response;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const fetchSession = async (
    fetchedId, isApplicationView = false, assignToSessionDetails = true,
  ) => {
    try {
      if (fetchedId === undefined || fetchedId === null || fetchedId === 0) {
        return session;
      }
      const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/${fetchedId}`;
      const response = await axios.get(url);
      const { data } = response;
      if (assignToSessionDetails && (data !== undefined || data !== null)) {
        session.sessionDetails = data;
        if (!isApplicationView) {
          await populateTreeTicks(
            session.sessionDetails.sessionComplexModels,
            session.sessionDetails.sessionUnitModels,
          );
        }
        uiPreProcessDates();
      }

      return data;
    } catch (error) {
      console.log(error);

      return error.response;
    }
  };

  const fetchSessionByName = async (sessionName) => {
    try {
      if (!sessionName) {
        return;
      }
      const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/GetSessionByName`;
      const response = await axios.get(url,
        {
          params: {
            sessionName,
          },
        });
      const { data } = response;
      if (data) {
        session.sessionDetails = data;
        uiPreProcessDates();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSessionNameIds = async (beforeDeadline = false, orderByStartDate = false) => {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/sessionNameId`,
        {
          params: {
            beforeDeadline,
            orderByStartDate,
          },
        },
      );
      const { data } = response;
      sessionNameIds.value = data.data;
      return data.data;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const getSessionNames = async () => {
    try {
      const response = await axios.get(`${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/sessionName`);
      const { data } = response;

      return data.data;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const updateSession = async (updateId) => {
    try {
      populateActiveComplexesAndUnits();
      const response = await axios.put(`${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/${updateId}`, session.sessionDetails);
      return response;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const getGenderIdentities = async () => {
    if (session.colleagueGenderOptions.length === 0) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/genderIdentities`);
        session.colleagueGenderOptions = response.data;
      } catch (error) {
        console.log(error);
        return error.response;
      }
    }

    return session.colleagueGenderOptions;
  };

  const getRoomRateTables = async () => {
    if (session.roomRateTables.length === 0) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/utility/roomRateTables`);
        session.roomRateTables = response.data;
      } catch (error) {
        console.log(error);
      }
    }
  };

  const getIsPlacedInPreviousSession = async (sessionId) => {
    const response = await axios.get(
      `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/isPlacedInPreviousSession`,
      {
        params: {
          sessionId,
        },
      },
    );
    const { data } = response;

    return data.data;
  };

  // @TODO: fetch this info from server once previous sessions
  const getPreviouslyPlaced = () => true;

  const resetSession = async () => {
    session.sessionId = 0;
    session.sessionDetails.sessionId = 0;
    session.sessionDetails.sessionName = '';
    session.sessionDetails.sessionType = '';
    session.sessionDetails.startDate = '';
    session.sessionDetails.endDate = '';
    session.sessionDetails.applicationLockDate = '';
    session.sessionDetails.isOpen = 'Closed';
    session.sessionDetails.unitTypes = [];
    session.sessionDetails.sessionUnitModels = [];
    session.sessionDetails.sessionComplexModels = [];
    session.sessionDetails.description = '';
    session.sessionDetails.moveInDate = '';
    session.sessionDetails.moveOutDate = '';
    session.sessionDetails.applicationDeadline = '';
    session.sessionDetails.confirmationDeadline = '';
    session.sessionDetails.genderOptions = [];
    session.sessionDetails.agreement = '';
    session.sessionDetails.agreementUri = '';
    session.sessionDetails.dssUri = '';
    session.sessionDetails.paymentDeadline = '';
    session.sessionDetails.paperworkDeadline = '';
    session.sessionDetails.complianceOrQuitDate = '';
    session.sessionDetails.priorityDeadline = '';
    session.sessionDetails.firstYearStudentMoveInDate = '';
    session.sessionDetails.returningStudentMoveInDate = '';
    session.sessionDetails.continuingMoveOutDate = '';
    session.sessionDetails.continuingMoveOutDate = '';
    session.sessionDetails.vaccinationUri = '';
    session.sessionDetails.depositAmount = '';
    session.sessionDetails.housingAgreementBlobId = null;
    session.sessionDetails.handbookAgreementBlobId = null;
    session.sessionDetails.sessionTerm = '';
    session.sessionDetails.previousSessionId = null;
    session.sessionDetails.eapSessionId = null;

    session.sessionDetails.emailTemplateApplicationSubmittedToHousingId = null;
    session.sessionDetails.emailTemplateApplicationSubmittedToStudentId = null;
    session.sessionDetails.emailTemplateDssRequestedToDssId = null;
    session.sessionDetails.emailTemplateDssRequestedToStudentId = null;
    session.sessionDetails.emailTemplatePlacementCompletedToStudentId = null;

    session.sessionDetails.postDeadlineApplicantWhitelist = [];
  };

  const getRoomRateTablesBySession = async (sessionId) => {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/sessions/roomRateTablesBySession`,
        {
          params: {
            sessionId,
          },
        },
      );
      const result = response.data;
      result.unshift('No buyout for this session');

      return result;
    } catch (error) {
      console.log(error);
    }
    return null;
  };

  return {
    ...toRefs(session),
    createSession,
    fetchSession,
    fetchSessionByName,
    fetchSessionNameIds,
    getGenderIdentities,
    getIsPlacedInPreviousSession,
    getPreviouslyPlaced,
    getRoomRateTables,
    getSessionNames,
    isSessionFormValid,
    resetSession,
    sessionNameIds,
    sessionIdSelected,
    showOnlyOpenSessions,
    updateSession,
    getRoomRateTablesBySession,
  };
}
